import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, Palette } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, errMsg } from "./api";

const Pill = ({ m }) => m ? (
  <span className="inline-flex items-center gap-1 rounded border border-zinc-700 px-1.5 py-0.5 text-[11px] text-zinc-300">
    <span className="h-2 w-2 rounded-sm" style={{ background: m.color }} />{m.short}
  </span>
) : null;

export default function AISuggestions({ projectId, suggestions, setSuggestions, zones, materials, onApply }) {
  const [loading, setLoading] = useState(false);
  const mat = (id) => materials.find((m) => m.id === id);
  const zone = (id) => zones.find((z) => z.id === id);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/suggest-materials`);
      setSuggestions(data);
      toast.success("AI suggestions ready");
    } catch (e) {
      toast.error(errMsg(e, "AI suggestion failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-zinc-800 bg-[#121215] p-5" data-testid="ai-suggestions-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-400" />
          <h3 className="font-[Manrope] font-semibold">AI Material Advisor</h3>
        </div>
        <Button data-testid="ai-suggest-btn" onClick={run} disabled={loading || !zones.length} size="sm" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-white">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {suggestions ? "Regenerate" : "Suggest materials"}
        </Button>
      </div>
      {!suggestions ? (
        <p className="text-sm text-zinc-500">{zones.length ? "Get zone-by-zone recommendations based on fire, wind, thermal and aesthetic needs." : "Add zones first."}</p>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-zinc-300" data-testid="ai-overall-strategy">{suggestions.overall_strategy}</p>
          {suggestions.palettes?.length > 0 && (
            <div className="grid gap-3 md:grid-cols-3">
              {suggestions.palettes.map((p, i) => (
                <div key={p.name} className="flex flex-col rounded-md border border-zinc-800 p-3">
                  <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold"><Palette className="h-3.5 w-3.5 text-sky-400" />{p.name}</div>
                  <p className="mb-3 text-xs text-zinc-500">{p.description}</p>
                  <Button data-testid={`apply-palette-${i}`} size="sm" variant="outline" className="mt-auto border-zinc-700 text-xs hover:text-white" onClick={() => onApply(p.mapping)}>Apply palette</Button>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {suggestions.zones?.map((s) => zone(s.zone_id) && (
              <div key={s.zone_id} data-testid={`ai-zone-suggestion-${s.zone_id}`} className="flex flex-col gap-2 rounded-md border border-zinc-800 p-3 md:flex-row md:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium text-white">{zone(s.zone_id).label}</span>
                    <Pill m={mat(s.recommended)} />
                    {s.alternatives?.map((a) => <span key={a} className="opacity-60"><Pill m={mat(a)} /></span>)}
                  </div>
                  <p className="mt-1 text-xs text-zinc-400">{s.rationale}{s.finish ? ` — ${s.finish}` : ""}</p>
                </div>
                <Button data-testid={`apply-suggestion-${s.zone_id}`} size="sm" variant="ghost" className="shrink-0 text-sky-300 hover:text-white" onClick={() => onApply({ [s.zone_id]: s.recommended }, { [s.zone_id]: s.finish })}>
                  <Check className="mr-1 h-3.5 w-3.5" />Apply
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
